import { type ProspectContactDto, useEventCreateEvent, useSalesGetProspect } from "@crowdcoursing/api/openapi";
import { skipToken } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSalesBubbleId } from "~hooks/useSalesBubbleId";
import { useToast } from "~hooks/useToast";
import { Route } from "~services/router/Route";
import { useContactModal } from "~ui/pages/SalesPortal/useContactModal";
import { useInteractionModal } from "~ui/pages/SalesPortal/useInteractionModal";
import { useNoteModal } from "~ui/pages/SalesPortal/useNoteModal";
import { useTaskModal, type UseTaskModalOptions } from "~ui/pages/SalesPortal/useTaskModal";

export interface UseProspectDetailsSlideoutOptions {
  onSalesTaskUpdated?: UseTaskModalOptions["onTaskUpdated"];
}

export const useProspectDetailsSlideout = (prospectId: string, options?: UseProspectDetailsSlideoutOptions) => {
  const navigate = useNavigate();
  const toast = useToast();
  const salesBubbleId = useSalesBubbleId();

  // State for contact info
  const [primaryEmail, setPrimaryEmail] = useState<string | null>(null);
  const [primaryPhone, setPrimaryPhone] = useState<string | null>(null);
  const [primaryContact, setPrimaryContact] = useState<
    | {
        id?: string;
        firstName?: string;
        lastName?: string;
        roleTitle?: string;
        isPrimary?: boolean;
      }
    | undefined
  >(undefined);

  // Fetch prospect details
  const { data: prospect } = useSalesGetProspect(
    salesBubbleId ? { pathParams: { bubbleId: salesBubbleId, id: prospectId } } : skipToken,
  );

  // Modal hooks
  const contactModal = useContactModal(prospectId);
  const noteModal = useNoteModal(prospectId);
  const interactionModal = useInteractionModal(prospectId);
  const taskModal = useTaskModal(prospectId, { onTaskUpdated: options?.onSalesTaskUpdated });

  // Create a unified modal interface
  const modals = {
    ...contactModal,
    closeInteractionModal: interactionModal.closeAddModal,
    closeNoteModal: noteModal.closeAddModal,
    closeTaskModal: taskModal.closeAddModal,
    handleAddInteraction: interactionModal.handleAddInteraction,
    handleAddNote: noteModal.handleAddNote,
    handleCreateTask: taskModal.handleCreateTask,
    openInteractionModal: interactionModal.openAddModal,
    openNoteModal: noteModal.openAddModal,
    openTaskModal: taskModal.openAddModal,
  };

  // Event creation mutation
  const { mutateAsync: createEvent } = useEventCreateEvent({
    onError: (error) => {
      console.error("Error creating event:", error);
      toast.error("Failed to create event. Please try again.");
    },
    onSuccess: (response) => {
      toast.success("Event created successfully!");
      navigate(Route.eventView({ eventId: response.id ?? "" }));
    },
  });

  // Handler for scheduling a meeting
  const handleScheduleMeeting = async () => {
    if (!prospect) {
      return;
    }

    await createEvent({
      body: {
        bubbleId: "c0524501-ff1c-4b76-b384-1317e342aa48",
        description:
          "Make sure to create a meeting time and RSVP so that it will show up on your schedule. You can also create a google meets and add it as a Resource here, as well. If you'd like to invite the person to this event, make sure to change the description and add an event photo before doing so.",
        isPublic: false,
        isSearchable: false,
        name: `CrowdCoursing Demo with ${prospect.name}`,
      },
    });
  };

  // Handle contact card click
  const handleContactCardClick = () => {
    if (primaryContact?.id) {
      // Create contact object
      const contactData: ProspectContactDto = {
        createdAt: "",
        email: primaryEmail || undefined,
        firstName: primaryContact.firstName,
        id: primaryContact.id,
        isPrimary: primaryContact.isPrimary,
        lastName: primaryContact.lastName,
        phone: primaryPhone || undefined,
        prospectId: prospectId,
        roleTitle: primaryContact.roleTitle,
        updatedAt: "",
      };

      // Open edit modal
      contactModal.openEditModal(contactData);
    }
  };

  // Update contact info when details change
  useEffect(() => {
    if (prospect?.contacts) {
      const contact = prospect.contacts.find((c) => c.isPrimary);
      setPrimaryEmail(contact?.email ?? null);
      setPrimaryPhone(contact?.phone ?? null);
      setPrimaryContact(
        contact
          ? {
              firstName: contact.firstName ?? undefined,
              id: contact.id,
              isPrimary: contact.isPrimary,
              lastName: contact.lastName ?? undefined,
              roleTitle: contact.roleTitle ?? undefined,
            }
          : undefined,
      );
    }
  }, [prospect]);

  return {
    contactModal,
    handleContactCardClick,
    handleScheduleMeeting,
    interactionModal,
    modals,
    noteModal,
    primaryContact,
    primaryEmail,
    primaryPhone,
    prospect,
    taskModal,
  };
};
